"use client"

import { cn } from "@/lib/utils"
import {
  FileText,
  FileSpreadsheet,
  Presentation,
  FileAudio,
  FileImage,
  FileDown,
  File,
  ExternalLink,
} from "lucide-react"

// ── Types ──

export interface DeliverableFile {
  name: string
  type: "docx" | "pdf" | "xlsx" | "pptx" | "mp3" | "png" | "jpg" | "zip" | "md" | "csv"
  size?: string  // e.g. "2.4 MB"
}

const FILE_ICON: Record<string, React.ElementType> = {
  docx: FileText,
  pdf: FileText,
  md: FileText,
  csv: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  pptx: Presentation,
  mp3: FileAudio,
  png: FileImage,
  jpg: FileImage,
  zip: FileDown,
}

const FILE_COLOR: Record<string, string> = {
  docx: "text-blue-500",
  pdf: "text-red-500",
  md: "text-gray-500",
  csv: "text-emerald-500",
  xlsx: "text-green-600",
  pptx: "text-orange-500",
  mp3: "text-purple-500",
  png: "text-pink-500",
  jpg: "text-pink-500",
  zip: "text-amber-600",
}

// ── Component ──

export function DeliverableCard({
  title,
  files,
  className,
}: {
  title?: string
  files: DeliverableFile[]
  className?: string
}) {
  return (
    <div className={cn(
      "rounded-xl border border-border/60 bg-card/60 backdrop-blur-sm overflow-hidden",
      className,
    )}>
      {/* Header */}
      {title && (
        <div className="px-4 py-2.5 border-b border-border/40 bg-secondary/20">
          <div className="flex items-center gap-2">
            <FileDown className="h-4 w-4 text-primary" />
            <span className="text-xs font-semibold text-foreground">{title}</span>
            <span className="text-micro px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium ml-auto">
              {files.length} 个文件
            </span>
          </div>
        </div>
      )}

      {/* File list */}
      <div className="divide-y divide-border/30">
        {files.map((file, i) => {
          const Icon = FILE_ICON[file.type] || File
          const color = FILE_COLOR[file.type] || "text-muted-foreground"

          return (
            <div
              key={`${file.name}-${i}`}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/20 transition-colors cursor-pointer group animate-in fade-in slide-in-from-left-1"
              style={{ animationDelay: `${i * 50}ms`, animationFillMode: "both" }}
            >
              {/* File icon */}
              <div className={cn("shrink-0", color)}>
                <Icon className="h-5 w-5" />
              </div>

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {file.name}
                </p>
                <p className="text-caption text-muted-foreground">
                  {file.type.toUpperCase()}{file.size ? ` · ${file.size}` : ""}
                </p>
              </div>

              {/* Action */}
              <button className="shrink-0 flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-secondary hover:text-foreground transition-all">
                <ExternalLink className="h-3.5 w-3.5" />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
